import type { GlobalThemeOverrides } from 'naive-ui';

// Naive UI mapping of the Simplex tokens in styles/tokens.css. Keep both in sync.
const brand = {
    primary: '#1AA38C',
    primaryHover: '#178F7B',
    primaryPressed: '#087F70',
    primaryButton: '#148575',
    primaryButtonHover: '#0F7566',
    primaryButtonPressed: '#0B6357',
    onPrimary: '#FFFFFF',
    accent: '#E7A43A',
    success: '#16804A',
    warning: '#A15C07',
    error: '#B42318',
};

const fontFamily = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";
const fontFamilyMono = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const focusRing = '0 0 0 3px rgba(26, 163, 140, .16)';
const menuShadow = '0 6px 24px rgba(19, 34, 54, .12), 0 2px 6px rgba(19, 34, 54, .06)';

// Naive parses button colors (seemly), so these must be literal colors, not CSS vars.
interface ButtonPalette { sunken: string; muted: string; link: string; linkHover: string }
const lightButton: ButtonPalette = { sunken: '#E8F0ED', muted: '#596B66', link: '#087F70', linkHover: '#006E60' };
const darkButton: ButtonPalette = { sunken: '#0B0D0C', muted: '#B4BFBA', link: '#67DEC9', linkHover: '#79C0C2' };

// Filled brand button (tokens.css --simplex-primary-button*), shared by primary, success and info.
const brandButton = (type: 'Primary' | 'Success' | 'Info', p: ButtonPalette) => ({
    [`color${type}`]: brand.primaryButton,
    [`colorHover${type}`]: brand.primaryButtonHover,
    [`colorPressed${type}`]: brand.primaryButtonPressed,
    [`colorFocus${type}`]: brand.primaryButtonHover,
    [`colorDisabled${type}`]: p.sunken,
    [`textColor${type}`]: brand.onPrimary,
    [`textColorHover${type}`]: brand.onPrimary,
    [`textColorPressed${type}`]: brand.onPrimary,
    [`textColorFocus${type}`]: brand.onPrimary,
    [`textColorDisabled${type}`]: p.muted,
    [`border${type}`]: `1px solid ${brand.primaryButton}`,
    [`borderHover${type}`]: `1px solid ${brand.primaryButtonHover}`,
    [`borderPressed${type}`]: `1px solid ${brand.primaryButtonPressed}`,
    [`borderFocus${type}`]: `1px solid ${brand.primaryButtonHover}`,
    [`borderDisabled${type}`]: `1px solid ${p.sunken}`,
    [`rippleColor${type}`]: brand.primaryButton,
    [`textColorText${type}`]: p.link,
    [`textColorTextHover${type}`]: p.linkHover,
    [`textColorTextPressed${type}`]: p.linkHover,
    [`textColorTextFocus${type}`]: p.linkHover,
    [`textColorGhost${type}`]: p.link,
    [`textColorGhostHover${type}`]: p.linkHover,
    [`textColorGhostPressed${type}`]: p.linkHover,
    [`textColorGhostFocus${type}`]: p.linkHover,
});

// Unchecked controls reuse the input colors (inputColor, borderColor); checked state uses the filled-button brand color so the white mark reaches 4.5:1.
interface ControlPalette { fill: string; border: string; rail: string }
const lightControl: ControlPalette = { fill: '#FBFCFE', border: '#C6CFDD', rail: '#A9B4C4' };
const darkControl: ControlPalette = { fill: '#121614', border: '#56615B', rail: '#6E7A74' };

const inputBorder = (c: ControlPalette) => ({
    Input: { ...shared.Input, border: `1px solid ${c.border}` },
    InternalSelection: { ...shared.InternalSelection, border: `1px solid ${c.border}` },
});

const checkboxOverrides = (c: ControlPalette) => ({
    color: c.fill,
    colorChecked: brand.primaryButton,
    checkMarkColor: brand.onPrimary,
    border: `1px solid ${c.border}`,
    borderChecked: `1px solid ${brand.primaryButton}`,
    borderFocus: `1px solid ${brand.primaryButton}`,
    boxShadowFocus: focusRing,
});

const radioOverrides = (c: ControlPalette) => ({
    color: c.fill,
    boxShadow: `inset 0 0 0 1px ${c.border}`,
    boxShadowHover: `inset 0 0 0 1px ${brand.primaryButton}`,
    boxShadowActive: `inset 0 0 0 1px ${brand.primaryButton}`,
    boxShadowFocus: `inset 0 0 0 1px ${brand.primaryButton}, ${focusRing}`,
    dotColorActive: brand.primaryButton,
    buttonBorderColorActive: brand.primaryButton,
    buttonBorderColorHover: brand.primaryButton,
    buttonTextColorActive: brand.primaryButton,
    buttonTextColorHover: brand.primaryButton,
    buttonBoxShadowFocus: `inset 0 0 0 1px ${brand.primaryButton}, ${focusRing}`,
});

const switchOverrides = (c: ControlPalette) => ({
    railColor: c.rail,
    railColorActive: brand.primaryButton,
    loadingColor: brand.primaryButton,
    boxShadowFocus: focusRing,
});

const buttonOverrides = (p: ButtonPalette) => ({
    heightMedium: '38px',
    heightLarge: '44px',
    borderRadiusTiny: '8px',
    borderRadiusSmall: '8px',
    borderRadiusMedium: '9px',
    borderRadiusLarge: '10px',
    fontSizeMedium: '12px',
    fontWeight: '650',
    paddingMedium: '0 16px',
    // Disabled buttons stay readable: sunken fill instead of opacity (LAYOUT.md)
    opacityDisabled: '1',
    colorDisabled: p.sunken,
    textColorDisabled: p.muted,
    borderDisabled: `1px solid ${p.sunken}`,
    ...brandButton('Primary', p),
    ...brandButton('Success', p),
    ...brandButton('Info', p),
});

const shared: GlobalThemeOverrides = {
    common: {
        fontFamily,
        fontFamilyMono,
        fontSize: '14px',
        fontSizeMedium: '13px',
        fontWeightStrong: '650',
        borderRadius: '10px',
        borderRadiusSmall: '8px',
        primaryColor: brand.primary,
        primaryColorHover: brand.primaryHover,
        primaryColorPressed: brand.primaryPressed,
        primaryColorSuppl: brand.primary,
        infoColor: brand.primary,
        infoColorHover: brand.primaryHover,
        infoColorPressed: brand.primaryPressed,
        infoColorSuppl: brand.primary,
        successColor: brand.success,
        successColorHover: '#136D3F',
        successColorPressed: '#105C35',
        successColorSuppl: brand.success,
        warningColor: brand.warning,
        warningColorHover: '#8A4F06',
        warningColorPressed: '#734205',
        warningColorSuppl: brand.warning,
        errorColor: brand.error,
        errorColorHover: '#9A1E15',
        errorColorPressed: '#801912',
        errorColorSuppl: brand.error,
    },
    Input: {
        heightMedium: '44px',
        borderRadius: '10px',
        fontSizeMedium: '13px',
        boxShadowFocus: focusRing,
        borderFocus: `1px solid ${brand.primary}`,
        borderHover: `1px solid ${brand.primary}`,
    },
    InternalSelection: {
        heightMedium: '44px',
        borderRadius: '10px',
        fontSizeMedium: '13px',
        boxShadowFocus: focusRing,
        boxShadowActive: focusRing,
        borderFocus: `1px solid ${brand.primary}`,
        borderActive: `1px solid ${brand.primary}`,
        borderHover: `1px solid ${brand.primary}`,
    },
    InputNumber: {
        peers: { Input: { heightMedium: '44px', borderRadius: '10px', boxShadowFocus: focusRing } },
    },
    DatePicker: {
        peers: { Input: { heightMedium: '44px', borderRadius: '10px', boxShadowFocus: focusRing } },
    },
    TimePicker: {
        peers: { Input: { heightMedium: '44px', borderRadius: '10px', boxShadowFocus: focusRing } },
    },
    Card: {
        borderRadius: '12px',
        titleFontWeight: '700',
    },
    Dialog: {
        borderRadius: '16px',
    },
    Tag: {
        borderRadius: '20px',
        fontWeightStrong: '650',
    },
    Table: {
        borderRadius: '12px',
        thFontWeight: '750',
        fontSizeMedium: '12.5px',
        thPaddingMedium: '12px 16px',
        tdPaddingMedium: '14px 16px',
        thPaddingSmall: '8px 12px',
        tdPaddingSmall: '6px 12px',
    },
    DataTable: {
        borderRadius: '12px',
        thFontWeight: '750',
        fontSizeMedium: '12.5px',
    },
    Tabs: {
        tabFontWeightActive: '650',
        tabTextColorActiveLine: brand.primaryPressed,
        tabTextColorHoverLine: brand.primary,
        barColor: brand.primary,
    },
    Dropdown: {
        borderRadius: '12px',
        boxShadow: menuShadow,
    },
    Popover: {
        borderRadius: '12px',
        boxShadow: menuShadow,
    },
    Alert: {
        borderRadius: '12px',
    },
};

export const lightThemeOverrides: GlobalThemeOverrides = {
    ...shared,
    common: {
        ...shared.common,
        bodyColor: '#ECEFF6',
        cardColor: '#FFFFFF',
        modalColor: '#FFFFFF',
        popoverColor: '#FFFFFF',
        tableColor: '#FFFFFF',
        inputColor: '#FBFCFE',
        inputColorDisabled: '#E8F0ED',
        textColorBase: '#17332F',
        textColor1: '#17332F',
        textColor2: '#26363C',
        textColor3: '#596B66',
        textColorDisabled: '#596B66',
        placeholderColor: '#98A3A8',
        borderColor: '#DFE4EC',
        dividerColor: '#EEF1F5',
        tableHeaderColor: '#FAFBFD',
        hoverColor: '#F3F7F6',
    },
    Button: buttonOverrides(lightButton),
    ...inputBorder(lightControl),
    Checkbox: checkboxOverrides(lightControl),
    Radio: radioOverrides(lightControl),
    Switch: switchOverrides(lightControl),
    Table: {
        ...shared.Table,
        thColor: '#FAFBFD',
        // Muted text (tokens.css --simplex-muted); 10px headers need >= 4.5:1 on thColor.
        thTextColor: '#596B66',
        tdTextColor: '#303B4B',
        borderColor: '#EEF1F5',
    },
};

export const darkThemeOverrides: GlobalThemeOverrides = {
    ...shared,
    common: {
        ...shared.common,
        bodyColor: '#101312',
        cardColor: '#1B211F',
        modalColor: '#1B211F',
        popoverColor: '#1F2624',
        tableColor: '#1B211F',
        inputColor: '#121614',
        inputColorDisabled: '#0B0D0C',
        textColorBase: '#F2F5F3',
        textColor1: '#F2F5F3',
        textColor2: '#E1E7E4',
        textColor3: '#C0CAC5',
        textColorDisabled: '#C0CAC5',
        borderColor: '#3F4844',
        dividerColor: '#313935',
        tableHeaderColor: '#1F2624',
        hoverColor: '#232A27',
    },
    Button: buttonOverrides(darkButton),
    ...inputBorder(darkControl),
    Checkbox: checkboxOverrides(darkControl),
    Radio: radioOverrides(darkControl),
    Switch: switchOverrides(darkControl),
    Table: {
        ...shared.Table,
        thColor: '#1D2321',
        thTextColor: '#9EAAA5',
        tdTextColor: '#E1E7E4',
        borderColor: '#2A312E',
    },
};
